import React, { useMemo } from 'react';
import { Chip, Tooltip } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CreateIcon from '@mui/icons-material/Create';
import CheckIcon from '@mui/icons-material/Check';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import VerifiedIcon from '@mui/icons-material/Verified';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TextsmsIcon from '@mui/icons-material/Textsms';
import ErrorIcon from '@mui/icons-material/Error';
import { HourglassBottomOutlined } from '@mui/icons-material';

interface RecordingStatusSemaphoreProps {
  recordings: any[];
}

export const RecordingStatusSemaphore = ({
  recordings,
}: RecordingStatusSemaphoreProps) => {
  const recordingStatusCounts = useMemo(() => {
    if (!recordings || recordings.length === 0) {
      return {};
    }

    return recordings.reduce((acc, recording) => {
      const status = recording.status || 'unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [recordings]);

  return (
    <>
      {statuses
        .map(status => {
          const config =
            statusConfig[status as keyof typeof statusConfig];
          const count = recordingStatusCounts[status] || 0;

          if (count === 0) {
            return null;
          }

          return (
            <Tooltip key={status} title={`${config.label}: ${count}`}>
              <Chip
                icon={config.icon}
                label={`${count}`}
                sx={{
                  ...chipSx,
                  backgroundColor: config.color,
                }}
                size="medium"
                onClick={e => e.stopPropagation()}
              />
            </Tooltip>
          );
        })
        .filter(Boolean)}

      <Tooltip
        key="total"
        title={`Total: ${recordings?.length || 0}`}
      >
        <Chip
          icon={<AssessmentIcon />}
          label={`${recordings?.length || 0}`}
          sx={chipSx}
          size="medium"
          onClick={e => e.stopPropagation()}
        />
      </Tooltip>
    </>
  );
};

const chipSx = {
  mx: 0.5,
  color: 'white',
  '& .MuiChip-icon': {
    color: 'white',
    marginLeft: '.4rem',
    fontSize: '1.2rem',
  },
  '& .MuiChip-label': {
    padding: '0 10px',
    fontSize: '0.9rem',
    fontWeight: 'bold',
  },
  height: '28px',
  borderRadius: '14px',
};

const statuses = [
  'draft',
  'committed',
  'splitted',
  'validated',
  'uploaded',
  'transcribing',
  'transcribed',
  'failed',
];

export const statusConfig = {
  draft: {
    icon: <CreateIcon />,
    color: '#FFA726',
    label: 'Draft',
  },
  committed: {
    icon: <CheckIcon />,
    color: '#F57C00',
    label: 'Committed',
  },
  splitted: {
    icon: <CallSplitIcon />,
    color: '#1976D2',
    label: 'Splitted',
  },
  validated: {
    icon: <VerifiedIcon />,
    color: '#66BB6A',
    label: 'Validated',
  },
  uploaded: {
    icon: <CloudUploadIcon />,
    color: '#7E57C2',
    label: 'Uploaded',
  },
  transcribing: {
    icon: <HourglassBottomOutlined />,
    color: '#e81e9e',
    label: 'Transcribing',
  },
  transcribed: {
    icon: <TextsmsIcon />,
    color: '#26A69A',
    label: 'Transcribed',
  },
  failed: {
    icon: <ErrorIcon />,
    color: '#F44336',
    label: 'Failed',
  },
  unknown: {
    icon: <AssessmentIcon />,
    color: '#9E9E9E',
    label: 'Unknown',
  },
};
